/**
 * M1 aiAnalysisSettings + M3 aiOutputFormats の Firestore ローダー (Sprint BG-DataSettings 18)。
 *
 * 旧 `old/apps/user/src/lib/aiFormatLoader.ts` (L4 参照、docs/97 §3 移植ログ) の踏襲。
 *
 * 踏襲点:
 *   - settingsId → M1 (aiAnalysisSettings/{id}) → outputFormatId → M3 (aiOutputFormats/{id})
 *     の 2 step 解決
 *   - モジュールスコープ Map cache (セッション中の重複読込防止)
 *   - 404 / Firestore エラーは null 返却 (UI は fallback で続行)
 *
 * 変更点 (v2):
 *   - 型を `AiOutputFormatMaster` → `AiOutputFormat` に改名 (shared-types v2 命名)
 *   - cache を `loadAiOutputFormat` 内に閉じず module top-level に分離、test 用に `_clearCache` を export
 *   - console.error → console.warn (頻発する admin 未設定運用を考慮、debug noise 抑制)
 *
 * 廃止点:
 *   - 無し (v1 の挙動を 100% 維持)
 *
 * 関連 docs: docs/91 U-L10 (M3 resolver) / docs/04 §3-1 M1+M3。
 */
import { doc, getDoc } from 'firebase/firestore';
import type { AiAnalysisSettings, AiOutputFormat } from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

export interface AiFormatLoadResult {
  settings: AiAnalysisSettings | null;
  format: AiOutputFormat | null;
}

const _cache = new Map<string, AiFormatLoadResult>();

/**
 * settingsId から M1 を取得し、`settings.outputFormatId` があれば M3 も取得。
 * 同一セッション中は cache 経由で再取得しない。
 */
/**
 * @param outputFormatIdOverride 地区の出力フォーマットの差し替え (docs/29 §5-1)。指定時は解析設定の
 *   outputFormatId の代わりにこの出力フォーマットを読む。
 */
export async function loadAiOutputFormat(
  settingsId: string,
  outputFormatIdOverride?: string | null,
): Promise<AiFormatLoadResult> {
  const cacheKey = outputFormatIdOverride ? `${settingsId} ${outputFormatIdOverride}` : settingsId;
  const cached = _cache.get(cacheKey);
  if (cached) return cached;

  try {
    const settingsSnap = await getDoc(doc(db, 'aiAnalysisSettings', settingsId));
    if (!settingsSnap.exists()) {
      const result: AiFormatLoadResult = { settings: null, format: null };
      _cache.set(cacheKey, result);
      return result;
    }
    const settings = {
      id: settingsId,
      ...(settingsSnap.data() as Omit<AiAnalysisSettings, 'id'>),
    } as AiAnalysisSettings;

    let format: AiOutputFormat | null = null;
    const formatId = outputFormatIdOverride || settings.outputFormatId;
    if (formatId) {
      const formatSnap = await getDoc(doc(db, 'aiOutputFormats', formatId));
      if (formatSnap.exists()) {
        format = {
          id: formatId,
          ...(formatSnap.data() as Omit<AiOutputFormat, 'id'>),
        } as AiOutputFormat;
      }
    }

    const result: AiFormatLoadResult = { settings, format };
    _cache.set(cacheKey, result);
    return result;
  } catch (e) {
    // 旧は console.error。v2 は admin 未設定が頻発するため warn に降格 (debug noise 抑制)。
    console.warn('Failed to load AI output format:', e);
    return { settings: null, format: null };
  }
}

/** test 用 cache clear (production code からは呼ばない)。 */
export function _clearAiFormatLoaderCache(): void {
  _cache.clear();
}
